import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useSidebar } from '../../contexts/SidebarContext';
import crmService from '../../services/crmService';
import HeaderNavigation from '../../components/ui/HeaderNavigation';
import MainSidebar from '../../components/ui/MainSidebar';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import Icon from '../../components/AppIcon';
import LeadCard from './components/LeadCard';
import CallLogModal from './components/CallLogModal';
import PerformanceMetrics from './components/PerformanceMetrics';
import NotificationToast from '../../components/ui/NotificationToast';

const TelecallerDashboard = () => {
  const { user, userProfile, loading: authLoading, authError } = useAuth();
  const { isCollapsed } = useSidebar();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [assignedLeads, setAssignedLeads] = useState([]);
  const [filteredLeads, setFilteredLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [metrics, setMetrics] = useState({});

  // Modal states
  const [showCallLogModal, setShowCallLogModal] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);

  // Filter states
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    priority: 'all'
  });

  // Notification state
  const [notification, setNotification] = useState(null);

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 5000);
  };

  // Enhanced access control check with better error messaging
  const checkTelecallerAccess = () => {
    // If auth is still loading, don't show access denied yet
    if (authLoading) {
      return { hasAccess: null, message: null };
    }

    // If there's an auth error, show it
    if (authError) {
      return {
        hasAccess: false,
        message: `Authentication Error: ${authError}`
      };
    }

    // If no user is authenticated
    if (!user) {
      return {
        hasAccess: false,
        message: 'Please log in to access the telecaller dashboard.'
      };
    }

    // If no user profile is loaded
    if (!userProfile) {
      return {
        hasAccess: false,
        message: 'User profile not found. Please contact administrator.'
      };
    }

    // Check if user has telecaller role or super_admin role
    if (userProfile.role !== 'telecaller' && userProfile.role !== 'super_admin') {
      return {
        hasAccess: false,
        message: `Access Denied: This dashboard requires telecaller or super_admin role. Your current role: ${userProfile.role || 'unknown'}`
      };
    }

    return { hasAccess: true, message: null };
  };

  const accessCheck = checkTelecallerAccess();

  // Load assigned leads and metrics
  useEffect(() => {
    const loadData = async () => {
      if (!userProfile?.id) {
        console.log('TelecallerDashboard: userProfile.id is missing', { userProfile });
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const isSuperAdmin = userProfile.role === 'super_admin';

        console.log('TelecallerDashboard: Loading leads for user:', {
          id: userProfile.id,
          role: userProfile.role,
          email: userProfile.email,
          full_name: userProfile.full_name,
          isSuperAdmin
        });

        // For super_admin, show all leads; for telecaller, show only assigned leads
        const [leadsResult, metricsResult] = await Promise.all([
          isSuperAdmin
            ? crmService.getLeads() // Get all leads for super_admin
            : crmService.getAssignedLeads(userProfile.id), // Get assigned leads for telecaller
          isSuperAdmin
            ? Promise.resolve({ success: true, data: {} }) // Skip metrics for super_admin or show aggregate
            : crmService.getTelecallerMetrics(userProfile.id)
        ]);

        console.log('TelecallerDashboard: Leads result:', leadsResult);
        console.log('TelecallerDashboard: Metrics result:', metricsResult);

        if (leadsResult?.success) {
          const leads = leadsResult.data || [];
          console.log('TelecallerDashboard: Loaded', leads.length, 'leads');
          setAssignedLeads(leads);
          setFilteredLeads(leads);
        } else {
          console.error('TelecallerDashboard: Failed to load leads:', leadsResult?.error);
          setError(leadsResult?.error || 'Failed to load leads');
        }

        if (metricsResult?.success) {
          setMetrics(metricsResult.data || {});
        }

      } catch (error) {
        console.error('TelecallerDashboard: Error loading data:', error);
        setError('Failed to load telecaller data');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [userProfile?.id, userProfile?.role]);

  // Apply filters
  useEffect(() => {
    let filtered = [...assignedLeads];

    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(lead =>
        lead?.name?.toLowerCase().includes(searchLower) ||
        lead?.email?.toLowerCase().includes(searchLower) ||
        lead?.phone?.includes(searchLower)
      );
    }

    if (filters.status && filters.status !== 'all') {
      filtered = filtered.filter(lead => lead?.status === filters.status);
    }

    if (filters.priority && filters.priority !== 'all') {
      // Sort by priority: new leads first, then by last update
      filtered.sort((a, b) => {
        if (filters.priority === 'new') {
          if (a.status === 'new' && b.status !== 'new') return -1;
          if (a.status !== 'new' && b.status === 'new') return 1;
        }
        return new Date(b.updated_at) - new Date(a.updated_at);
      });
    }

    setFilteredLeads(filtered);
  }, [assignedLeads, filters]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      status: '',
      priority: 'all'
    });
  };

  const handleCallLead = (lead) => {
    setSelectedLead(lead);
    setShowCallLogModal(true);
  };

  const handleCallLogged = async (callData, leadUpdate = null) => {
    try {
      const isSuperAdmin = userProfile.role === 'super_admin';

      // For super_admin, use the lead's assigned telecaller if available, otherwise use super_admin's ID
      const telecallerId = isSuperAdmin && selectedLead?.assigned_telecaller_id
        ? selectedLead.assigned_telecaller_id
        : userProfile.id;

      // Log the call
      const callResult = await crmService.logCall({
        lead_id: selectedLead.id,
        telecaller_id: telecallerId,
        ...callData
      });

      if (!callResult?.success) {
        showNotification('Failed to log call', 'error');
        return;
      }

      // Update lead if validation was performed
      if (leadUpdate) {
        const updateResult = await crmService.updateLead(selectedLead.id, leadUpdate);
        if (!updateResult?.success) {
          showNotification('Call logged but failed to update lead status', 'warning');
          return;
        }
      }

      // Refresh data
      const [leadsResult, metricsResult] = await Promise.all([
        isSuperAdmin
          ? crmService.getLeads() // Get all leads for super_admin
          : crmService.getAssignedLeads(userProfile.id), // Get assigned leads for telecaller
        isSuperAdmin
          ? Promise.resolve({ success: true, data: {} }) // Skip metrics for super_admin
          : crmService.getTelecallerMetrics(userProfile.id)
      ]);

      if (leadsResult?.success) {
        setAssignedLeads(leadsResult.data || []);
      }

      if (metricsResult?.success) {
        setMetrics(metricsResult.data || {});
      }

      setShowCallLogModal(false);
      setSelectedLead(null);
      showNotification('Call logged successfully');

    } catch (error) {
      console.log('Call logging error:', error);
      showNotification('Failed to log call', 'error');
    }
  };

  const getLeadPriorityIcon = (lead) => {
    if (lead?.status === 'new') return 'AlertCircle';
    if (lead?.status === 'contacted' && !lead?.lead_calls?.some(call => call.is_valid_lead === true)) {
      return 'Clock';
    }
    return 'User';
  };

  const getLeadPriorityColor = (lead) => {
    if (lead?.status === 'new') return 'text-red-500';
    if (lead?.status === 'contacted') return 'text-orange-500';
    return 'text-blue-500';
  };

  const activeFiltersCount = Object.values(filters).filter(value => value !== '' && value !== 'all').length;

  // Check if user is telecaller
  if (accessCheck.hasAccess === false) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-6">
          <Icon name="Lock" size={64} className="text-muted-foreground mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-foreground mb-4">Access Denied</h2>
          <p className="text-muted-foreground mb-6">
            {accessCheck.message}
          </p>

          {/* Debugging information for development */}
          <div className="bg-muted/10 border border-muted rounded-lg p-4 text-left text-sm">
            <h3 className="font-semibold mb-2">Debug Information:</h3>
            <div className="space-y-1 text-muted-foreground">
              <p>• Auth Loading: {authLoading ? 'Yes' : 'No'}</p>
              <p>• User Authenticated: {user ? 'Yes' : 'No'}</p>
              <p>• User Profile Loaded: {userProfile ? 'Yes' : 'No'}</p>
              <p>• Current Role: {userProfile?.role || 'Not set'}</p>
              <p>• Expected Role: telecaller</p>
              {authError && <p>• Auth Error: {authError}</p>}
            </div>
          </div>

          {/* Help instructions */}
          <div className="mt-6 text-left">
            <h4 className="font-medium text-foreground mb-2">To resolve this issue:</h4>
            <ol className="text-sm text-muted-foreground space-y-1 list-decimal list-inside">
              <li>Ensure you're logged in with a telecaller account</li>
              <li>Contact administrator to assign telecaller role</li>
              <li>Try logging out and logging back in</li>
              <li>Use test credential: telecaller@laborpro.com / Telecaller@123</li>
            </ol>
          </div>

          <div className="mt-6 flex gap-3 justify-center">
            <Button
              variant="outline"
              onClick={() => window.location.reload()}
            >
              Retry
            </Button>
            <Button
              variant="default"
              onClick={() => window.location.href = '/erp/login-authentication'}
            >
              Login Page
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Show loading state while auth is initializing
  if (accessCheck.hasAccess === null) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex items-center gap-3">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          <p className="text-muted-foreground">Checking telecaller access...</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex items-center gap-3">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          <p className="text-muted-foreground">Loading lead calls dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <HeaderNavigation
        onSidebarToggle={() => setSidebarOpen(true)}
        user={user}
        userProfile={userProfile}
      />

      <MainSidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      />

      <main className={`min-h-screen pt-16 transition-all duration-300 ${isCollapsed ? 'lg:ml-16' : 'lg:ml-64'}`}>
        <div className="p-4 sm:p-6 lg:p-8">
          {/* Header */}
          <div className="mb-6 sm:mb-8">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-foreground">
                  Lead Calls Dashboard
                </h1>
                <p className="text-muted-foreground mt-1">
                  Welcome back, {userProfile?.full_name}! {userProfile?.role === 'super_admin' ? 'View and manage all leads across the system.' : 'Manage your assigned leads and track performance.'}
                </p>
              </div>

              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Icon name="Clock" size={16} />
                <span>Last updated: {new Date().toLocaleTimeString()}</span>
              </div>
            </div>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
              <div className="flex items-center gap-2 text-destructive">
                <Icon name="AlertCircle" size={16} />
                <p className="font-medium">Error loading data</p>
              </div>
              <p className="text-sm text-destructive/80 mt-1">{error}</p>
            </div>
          )}

          {/* Performance Metrics */}
          <div className="mb-6 sm:mb-8">
            <PerformanceMetrics metrics={metrics} />
          </div>

          {/* Filters */}
          <div className="mb-6 p-4 sm:p-6 bg-card border border-border rounded-lg">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-foreground">Lead Filters</h3>
              {activeFiltersCount > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearFilters}
                  className="text-muted-foreground"
                >
                  Clear ({activeFiltersCount})
                </Button>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="sm:col-span-2">
                <Input
                  placeholder="Search leads by name, email, or phone..."
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                />
              </div>

              <Select
                value={filters.status}
                onValueChange={(value) => handleFilterChange('status', value)}
                placeholder="Status"
              >
                <option value="">All Statuses</option>
                <option value="new">New</option>
                <option value="contacted">Contacted</option>
                <option value="validated">Validated</option>
                <option value="rejected">Rejected</option>
              </Select>

              <Select
                value={filters.priority}
                onValueChange={(value) => handleFilterChange('priority', value)}
                placeholder="Priority"
              >
                <option value="all">All Priorities</option>
                <option value="new">New Leads First</option>
                <option value="recent">Recently Updated</option>
              </Select>
            </div>
          </div>

          {/* Leads Table */}
          <div className="bg-card border border-border rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-foreground uppercase">Lead Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-foreground uppercase">Contact</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-foreground uppercase">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-foreground uppercase">Experience</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-foreground uppercase">Skills</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-foreground uppercase">Last Call</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-foreground uppercase">Total Calls</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-foreground uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {filteredLeads.length === 0 ? (
                    <tr>
                      <td colSpan="8" className="px-6 py-12 text-center text-muted-foreground">
                        <Icon name="Users" size={48} className="mx-auto mb-4 opacity-50" />
                        <h3 className="text-lg font-medium text-foreground mb-2">
                          {assignedLeads.length === 0
                            ? (userProfile?.role === 'super_admin' ? 'No leads found' : 'No leads assigned')
                            : 'No leads match your filters'}
                        </h3>
                        <p className="text-sm max-w-md mx-auto">
                          {assignedLeads.length === 0
                            ? (userProfile?.role === 'super_admin'
                              ? 'There are no leads in the system yet. Leads will appear here once they are created.'
                              : 'You will see your assigned leads here once they are assigned by HR or admin.')
                            : 'Try adjusting your search criteria or clearing the filters.'}
                        </p>
                        {activeFiltersCount > 0 && assignedLeads.length > 0 && (
                          <Button
                            variant="outline"
                            onClick={clearFilters}
                            className="mt-4"
                          >
                            Clear Filters
                          </Button>
                        )}
                      </td>
                    </tr>
                  ) : (
                    filteredLeads.map((lead) => {
                      const getStatusColor = (status) => {
                        const colors = {
                          new: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',
                          contacted: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300',
                          validated: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
                          rejected: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300',
                          shortlisted: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300',
                          sent_to_client: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-300',
                          placed: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-300'
                        };
                        return colors[status] || 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300';
                      };

                      const formatStatus = (status) => {
                        if (!status) return '';
                        return status.split('_').map(word =>
                          word.charAt(0).toUpperCase() + word.slice(1)
                        ).join(' ');
                      };

                      const getLastCallInfo = () => {
                        if (!lead?.lead_calls || lead.lead_calls.length === 0) {
                          return { text: 'No calls', color: 'text-muted-foreground' };
                        }
                        const lastCall = lead.lead_calls[0];
                        const callDate = new Date(lastCall.call_date);
                        // Display actual call time in local format, e.g., "2026-06-09 14:30"
                        const formatted = callDate.toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' });
                        return { text: formatted, color: 'text-muted-foreground' };
                      };

                      const getSkills = () => {
                        let skills = [];
                        if (lead?.skills) {
                          if (Array.isArray(lead.skills)) {
                            skills = lead.skills;
                          } else if (typeof lead.skills === 'string') {
                            try {
                              const parsed = JSON.parse(lead.skills);
                              skills = Array.isArray(parsed) ? parsed : [];
                            } catch {
                              skills = lead.skills.split(',').map(s => s.trim()).filter(Boolean);
                            }
                          }
                        }
                        return skills;
                      };

                      const skills = getSkills();
                      const lastCallInfo = getLastCallInfo();
                      const callStats = {
                        total: lead?.lead_calls?.length || 0,
                        answered: lead?.lead_calls?.filter(call => call.outcome === 'answered').length || 0,
                        valid: lead?.lead_calls?.filter(call => call.is_valid_lead === true).length || 0
                      };
                      const isHighPriority = lead?.status === 'new' ||
                        (lead?.status === 'contacted' && !lead?.lead_calls?.some(call => call.is_valid_lead === true));

                      return (
                        <tr
                          key={lead.id}
                          className={`hover:bg-muted/30 transition-colors ${isHighPriority ? 'bg-orange-50/30 dark:bg-orange-950/30' : ''
                            }`}
                        >
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-3">
                              <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                                <span className="text-xs font-medium text-primary">
                                  {lead?.name?.charAt(0)?.toUpperCase() || '?'}
                                </span>
                              </div>
                              <div>
                                <div className="font-medium text-foreground">{lead?.name || 'Unknown'}</div>
                                {isHighPriority && (
                                  <div className="flex items-center gap-1 text-xs text-orange-600 mt-1">
                                    <Icon name="Zap" size={10} />
                                    <span>High Priority</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm">
                              <div className="text-foreground font-medium">{lead?.phone || '-'}</div>
                              {lead?.email && (
                                <div className="text-muted-foreground text-xs truncate max-w-[200px]">{lead.email}</div>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${getStatusColor(lead?.status)}`}>
                              {formatStatus(lead?.status)}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-sm text-foreground">
                            {lead?.experience_years || 0} years
                            {lead?.skill_level && (
                              <div className="text-xs text-muted-foreground capitalize mt-1">
                                {lead.skill_level}
                              </div>
                            )}
                          </td>
                          <td className="px-6 py-4">
                            {skills.length > 0 ? (
                              <div className="flex flex-wrap gap-1 max-w-[200px]">
                                {skills.slice(0, 2).map((skill, index) => (
                                  <span
                                    key={index}
                                    className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-primary/10 text-primary"
                                  >
                                    {skill}
                                  </span>
                                ))}
                                {skills.length > 2 && (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-muted text-muted-foreground">
                                    +{skills.length - 2}
                                  </span>
                                )}
                              </div>
                            ) : (
                              <span className="text-sm text-muted-foreground">-</span>
                            )}
                          </td>
                          <td className="px-6 py-4 text-sm">
                            <div className={`${lastCallInfo.color}`}>
                              {lastCallInfo.text}
                            </div>
                            {callStats.answered > 0 && (
                              <div className="text-xs text-muted-foreground mt-1">
                                {callStats.answered} answered
                              </div>
                            )}
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm">
                              <div className="font-medium text-foreground">{callStats.total}</div>
                              {callStats.valid > 0 && (
                                <div className="text-xs text-green-600 mt-1">
                                  {callStats.valid} valid
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <Button
                              onClick={() => handleCallLead(lead)}
                              size="sm"
                              variant={lead?.status === 'new' ? 'default' : 'outline'}
                              className="flex items-center gap-2"
                            >
                              <Icon name="Phone" size={14} />
                              {lead?.status === 'new' ? 'Call' : 'Log Call'}
                            </Button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </main>

      {/* Call Log Modal */}
      {showCallLogModal && selectedLead && (
        <CallLogModal
          lead={selectedLead}
          onClose={() => setShowCallLogModal(false)}
          onCallLogged={handleCallLogged}
        />
      )}

      {/* Notification */}
      {notification && (
        <NotificationToast
          message={notification.message}
          type={notification.type}
          onClose={() => setNotification(null)}
        />
      )}
    </div>
  );
};

export default TelecallerDashboard;